import { COUNTRY_CODES } from '@shared/constants/CountryCodes';
import { dynamicTranslations } from "@shared/utils/dynamicTranslation";
import React, { useState } from 'react';
import { FlatList, Keyboard, Modal, Text, TextInput, TouchableOpacity, View } from 'react-native';


export interface IPhoneInputProps {
    hasError?: boolean;
    errorMessage?: string;
    prefixValue?: string;
    phoneValue?: string;
    label?: string;
    onPrefixChange?: (prefix: string) => void;
    onPhoneChange?: (phone: string) => void;
    onFocus?: () => void;
}

export const PhoneInput = ({
    hasError,
    errorMessage,
    prefixValue = '+39',
    phoneValue = '',
    label,
    onPrefixChange,
    onPhoneChange,
    onFocus,
}: IPhoneInputProps) => {
    const { t } = dynamicTranslations();
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const handlePrefixSelect = (prefix: string) => {
        onPrefixChange?.(prefix);
        setIsDropdownVisible(false);
    };

    const handleOpenDropdown = () => {
        Keyboard.dismiss();
        setIsDropdownVisible(true);
    };

    const handleFocus = () => {
        setIsFocused(true);
        onFocus?.();
    };

    const handleBlur = () => {
        setIsFocused(false);
    };

    const handlePhoneChange = (text: string) => {
        onPhoneChange?.(text);
    };

    const selectedCountry = COUNTRY_CODES.find(item => item.code === prefixValue) || COUNTRY_CODES[0];

    return (
        <View className="mb-4">
            {label && (
                <Text className={`text-sm font-medium mb-2 ${hasError ? 'text-error-700' : 'text-black'}`}>
                    {t(label)}
                </Text>
            )}

            <View className="flex-row gap-2">
                <TouchableOpacity
                    className={`flex-row items-center bg-gray-100 px-3 py-3 rounded-lg border-2 min-w-[80px] ${
                        hasError ? 'border-error-700' : isFocused ? 'border-primary-700' : 'border-gray-300'
                    }`}
                    onPress={handleOpenDropdown}
                >
                    <Text className="text-base mr-1">{selectedCountry.flag}</Text>
                    <Text className="text-base text-black font-medium mr-1">{selectedCountry.code}</Text>
                    <Text className="text-[10px] text-gray-500">▼</Text>
                </TouchableOpacity>

                <TextInput
                    value={phoneValue}
                    className={`flex-1 border-2 rounded-lg px-3 py-3 text-base text-black ${
                        hasError ? 'border-error-700' : isFocused ? 'border-primary-700' : 'border-gray-300'
                    }`}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChangeText={handlePhoneChange}
                    keyboardType="phone-pad"
                    returnKeyType="done"
                    placeholder="000 000 0000"
                />
            </View>

            {hasError && errorMessage && (
                <Text className="text-xs text-error-700 mt-1">
                    {t(errorMessage)}
                </Text>
            )}

            <Modal
                visible={isDropdownVisible}
                transparent={true}
                animationType="fade"
                onRequestClose={() => setIsDropdownVisible(false)}
            >
                <TouchableOpacity
                    className="flex-1 bg-black/50 justify-center items-center"
                    onPress={() => setIsDropdownVisible(false)}
                >
                    <View className="bg-white rounded-lg max-h-[300px] w-[280px] shadow-lg">
                        <FlatList
                            data={COUNTRY_CODES}
                            keyExtractor={(item) => item.code}
                            renderItem={({ item }) => (
                                <TouchableOpacity
                                    className="flex-row items-center px-4 py-3 border-b border-gray-200"
                                    onPress={() => handlePrefixSelect(item.code)}
                                >
                                    <Text className="text-base">{item.flag}</Text>
                                    <Text className="flex-1 text-base text-black ml-2">{item.country}</Text>
                                    <Text className="text-base text-gray-500 font-medium">{item.code}</Text>
                                </TouchableOpacity>
                            )}
                        />
                    </View>
                </TouchableOpacity>
            </Modal>
        </View>
    );
};
